/**
 * Emotion Theme Override
 * This module provides a workaround for the "Cannot assign to read only property 'theme'" error
 * by intercepting emotion's theme attachment and providing a safe alternative
 */

interface Theme {
  breakpoints?: {
    down: (key: string) => string;
    up: (key: string) => string;
    between: (start: string, end: string) => string;
    only: (key: string) => string;
  };
  [key: string]: any;
}

interface EmotionCache {
  insert: (selector: string, serialized: any, sheet: any, shouldCache: boolean) => void;
}

declare global {
  interface Window {
    __emotion_cache__?: EmotionCache;
  }
}

/**
 * Safe theme attachment that prevents read-only property errors
 */
const safeAttachTheme = (theme: Theme | null | undefined): Theme | null | undefined => {
  if (!theme || typeof theme !== 'object') return theme;

  // Create a mutable copy of theme if it's frozen/sealed
  if (Object.isFrozen(theme) || Object.isSealed(theme)) {
    const mutableTheme: Theme = {};

    // Copy all properties and methods
    for (const key in theme) {
      try {
        if (typeof theme[key] === 'function') {
          mutableTheme[key] = theme[key].bind(theme);
        } else if (theme[key] && typeof theme[key] === 'object') {
          mutableTheme[key] = { ...theme[key] };
        } else {
          mutableTheme[key] = theme[key];
        }
      } catch (e) {
        // If we can't copy a property, skip it
        console.warn('Could not copy theme property:', key, e);
      }
    }

    // Ensure critical theme methods are preserved
    if (theme.breakpoints && typeof theme.breakpoints.down === 'function') {
      mutableTheme.breakpoints = {
        ...theme.breakpoints,
        down: theme.breakpoints.down.bind(theme.breakpoints),
        up: theme.breakpoints.up.bind(theme.breakpoints),
        between: theme.breakpoints.between.bind(theme.breakpoints),
        only: theme.breakpoints.only.bind(theme.breakpoints)
      };
    }

    return mutableTheme;
  }

  return theme;
};

/**
 * Initialize the emotion theme override
 * This should be called before any styled components are rendered
 */
export const initEmotionThemeOverride = () => {
  if (typeof window !== 'undefined') {
    // Try to override emotion's attachTheme function if it exists
    try {
      // Look for emotion's attachTheme in the global scope
      const emotionCache = window.__emotion_cache__;
      if (emotionCache) {
        // Override theme attachment at the cache level
        const originalInsert = emotionCache.insert;
        emotionCache.insert = function (selector: string, serialized: any, sheet: any, shouldCache: boolean) {
          // Apply safe theme processing to serialized styles
          if (serialized && serialized.styles) {
            try {
              // Process the styles to handle theme references safely
              serialized.styles = serialized.styles.replace(/theme\s*=>\s*theme\./g, 'theme => (theme && theme.');
            } catch (e) {
              // If processing fails, continue with original
            }
          }
          return originalInsert.call(this, selector, serialized, sheet, shouldCache);
        };
      }
    } catch (e) {
      console.warn('Could not override emotion theme attachment:', e);
    }
  }
};

export default safeAttachTheme;
